import { useContext } from "react"
import { bool, func, node, object } from "prop-types"
import { CurrentGroupContext } from "source/groups/my/groups"
import {
  BreadcrumbNavigationWithMe,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
  Icon,
} from "source/shared/components"
import {
  Menu,
  MenuButton,
  MenuList,
  MenuLink,
  MenuItem,
} from "@reach/menu-button"
import { Link, useNavigate } from "react-router-dom"
import { Modal } from "source/shared/components/Modal"
import { LeaveGroupModalContent } from "source/groups/components/LeaveGroupModalContent"

function buildChatDrawerFragmentRoute(group) {
  const chatGroupGraphId = `Groups-Group-${group.id}`
  return `#chat=/?filter=${chatGroupGraphId}`
}

function PcoChatLink({ group, disabled }) {
  const chatDrawerFragmentRoute = buildChatDrawerFragmentRoute(group)

  if (disabled) {
    return (
      <button
        className="minor-btn secondary-btn btn d-n d-if@sm"
        aria-label="Chat not enabled for this group"
        disabled
        title="Not enabled for this group"
      >
        <Icon
          symbol="general#text-message"
          className="mr-4p"
          style={{ fontSize: 9 }}
          aria-hidden
        />
        Chat
      </button>
    )
  } else {
    return (
      <Link
        to={chatDrawerFragmentRoute}
        onClick={(e) => {
          e.preventDefault()
          window.location.hash = chatDrawerFragmentRoute
        }}
        className="minor-btn secondary-btn btn d-n d-if@sm"
        aria-label="Chat with group"
      >
        <Icon
          symbol="general#text-message"
          className="mr-4p"
          style={{ fontSize: 9 }}
          aria-hidden
        />
        Chat with group
      </Link>
    )
  }
}

PcoChatLink.propTypes = {
  disabled: bool.isRequired,
  group: object.isRequired,
}

function PcoChatMenuItem({ disabled, group }) {
  if (disabled) return null

  return (
    <MenuItem
      className="d-b d-n@sm"
      onSelect={() =>
        (window.location.hash = buildChatDrawerFragmentRoute(group))
      }
    >
      Chat with group
    </MenuItem>
  )
}

PcoChatMenuItem.propTypes = {
  disabled: bool.isRequired,
  group: object.isRequired,
}

export default function MyGroupBreadcrumbs({ children, showActions }) {
  const navigate = useNavigate()
  const group = useContext(CurrentGroupContext)
  const published = group.enrollment_status !== "private"
  const emailNewPath = `${group.base_path}/members/email/new`
  const chatEnabled = group.abilities.conversations.can_read

  return (
    <BreadcrumbNavigationWithMe>
      <BreadcrumbPage linkUrl="/groups/profile" pageText="My groups" />
      <BreadcrumbDividerIcon />
      <BreadcrumbPage
        linkUrl={group.base_path}
        pageText={group.name}
        isActive
      />
      {children}
      {showActions && (
        <div className="ml-a d-f ai-c jc-c gap-2">
          <Modal>
            <Menu>
              <MenuButton className="minor-btn secondary-btn btn">
                Actions
                <span className="fs-6 pl-4p dropdown-trigger__icon">
                  <Icon symbol="general#down-chevron" aria-hidden />
                </span>
              </MenuButton>
              <MenuList>
                {<PcoChatMenuItem group={group} disabled={!chatEnabled} />}
                {group.abilities.email_messages.can_create && (
                  <MenuLink
                    as={Link}
                    to={emailNewPath}
                    state={{ prevPath: window.location.pathname }}
                  >
                    Email group
                  </MenuLink>
                )}
                {published && (
                  <MenuLink href={group.church_center_web_url}>
                    View public group page
                  </MenuLink>
                )}
                {group.abilities.can_manage && (
                  <MenuLink href={group.planning_center_url}>
                    Manage on Planning Center
                  </MenuLink>
                )}
                <Modal.Trigger as={MenuItemTrigger} className="dropdown__item">
                  Leave group…
                </Modal.Trigger>
              </MenuList>
            </Menu>
            <LeaveGroupModalContent
              groupId={group.id}
              groupName={group.name}
              afterSubmit={() => navigate("/groups/profile")}
            />
          </Modal>
          <PcoChatLink group={group} disabled={!chatEnabled} />
        </div>
      )}
    </BreadcrumbNavigationWithMe>
  )
}
MyGroupBreadcrumbs.propTypes = {
  children: node,
  showActions: bool.isRequired,
}

function MenuItemTrigger({ onClick, ...rest }) {
  return <MenuItem onSelect={onClick} {...rest} />
}
MenuItemTrigger.propTypes = {
  onClick: func.isRequired,
}
